import { Button } from "@/components/ui/button";
import { AlertCircle, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";

export type ErrorContext =
  | "telemetry"
  | "lap times"
  | "race control"
  | "standings"
  | "session data"
  | "schedule"
  | "driver data"
  | "track map"
  | "data";

interface ErrorWithRetryProps {
  message?: string;
  /**
   * Optional label for what failed. When provided (and `message` is not),
   * the visible message becomes "Failed to load <context>" and the helper
   * text references the context, so users know which widget is broken.
   */
  context?: ErrorContext;
  onRetry?: () => void;
  isRetrying?: boolean;
  className?: string;
  compact?: boolean;
}

export function ErrorWithRetry({
  message,
  context,
  onRetry,
  isRetrying = false,
  className,
  compact = false,
}: ErrorWithRetryProps) {
  const resolvedMessage =
    message ?? (context ? `Failed to load ${context}` : "Failed to load data");
  const helperText = context
    ? `We couldn't reach the ${context} feed. Check your connection and try again.`
    : "Check your connection and try again";

  if (compact) {
    return (
      <div
        role="alert"
        aria-live="polite"
        className={cn("flex items-center gap-2 text-destructive text-sm", className)}
      >
        <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="truncate">{resolvedMessage}</span>
        {onRetry && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRetry}
            disabled={isRetrying}
            className="h-7 px-2 ml-auto shrink-0"
            aria-label={`Retry loading ${context ?? "data"}`}
          >
            <RefreshCw
              className={cn("h-3 w-3", isRetrying && "animate-spin")}
              aria-hidden="true"
            />
            <span className="ml-1 text-xs">Retry</span>
          </Button>
        )}
      </div>
    );
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn("flex flex-col items-center justify-center gap-4 py-8 text-center", className)}
    >
      <div className="rounded-full bg-destructive/10 p-3">
        <AlertCircle className="h-6 w-6 text-destructive" aria-hidden="true" />
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">{resolvedMessage}</p>
        <p className="text-xs text-muted-foreground">{helperText}</p>
      </div>
      {onRetry && (
        <Button
          variant="outline"
          size="sm"
          onClick={onRetry}
          disabled={isRetrying}
          className="gap-2"
          aria-label={`Retry loading ${context ?? "data"}`}
        >
          <RefreshCw
            className={cn("h-4 w-4", isRetrying && "animate-spin")}
            aria-hidden="true"
          />
          {isRetrying ? "Retrying..." : "Try Again"}
        </Button>
      )}
    </div>
  );
}

// Inline version for card headers
export function RetryButton({
  onRetry,
  isRetrying = false,
  className,
}: {
  onRetry: () => void;
  isRetrying?: boolean;
  className?: string;
}) {
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={onRetry}
      disabled={isRetrying}
      className={cn("h-7 w-7", className)}
      title="Retry"
    >
      <RefreshCw className={cn("h-3.5 w-3.5", isRetrying && "animate-spin")} />
    </Button>
  );
}
